import { useQuery } from '@edreamsodigeo/frontend-api-client';
import {
  useLogFrontendApiError,
  usePersistedSessionState,
} from '@edreamsodigeo/frontend-core';

import { useGetTokenFromURL } from 'clientState/hooks';

import GET_TRIP_QUERY, { GET_TRIP_BY_TOKEN_QUERY } from './operations/query';

import type {
  AdditionalProducts_GetTripByTokenQuery,
  AdditionalProducts_GetTripByTokenQueryVariables,
  GetTripQuery,
  QueryGetTripArgs,
  TripResponse,
} from '#src/generated/graphql';
import type { FeapiError } from '@edreamsodigeo/frontend-api-client';

const useGetBookingIdFromSessionStorage = () => {
  const [bookingInfo] = usePersistedSessionState('bookingInfo');
  const bookingId = bookingInfo?.bookingBasicInfo?.id || '';
  const email = bookingInfo?.buyer?.mail || '';
  return { bookingId, email };
};

const useGetTrip = (): {
  data: TripResponse | null;
  errors: FeapiError[] | null;
  loading: boolean;
} => {
  const token = useGetTokenFromURL();
  const { bookingId, email } = useGetBookingIdFromSessionStorage();
  const queryParams = token
    ? {
        query: GET_TRIP_BY_TOKEN_QUERY,
        skip: !token,
        variables: { token },
      }
    : {
        query: GET_TRIP_QUERY,
        skip: !bookingId || !email,
        variables: { bookingId, email },
      };

  const result = useQuery<
    GetTripQuery | AdditionalProducts_GetTripByTokenQuery,
    QueryGetTripArgs | AdditionalProducts_GetTripByTokenQueryVariables
  >(queryParams.query, {
    skip: queryParams.skip,
    variables: queryParams.variables,
  });

  const { loading, errors, data } = result;

  useLogFrontendApiError(result);

  let tripResponse: TripResponse | null = null;
  if (data) {
    tripResponse = token
      ? (data as { getTripByToken: TripResponse })?.getTripByToken
      : (data as { getTrip: TripResponse })?.getTrip;
  }

  return {
    data: tripResponse,
    errors: errors,
    loading: loading,
  };
};

export default useGetTrip;
